import { settingsActions } from 'saur-viseau-state-management/lib/settings/state';
import Keycloak from 'keycloak-js';

/** Décode le payload d'un JWT sans vérification de signature */
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
};

/**
 * Construit l'URL de logout Keycloak conforme OIDC RP-Initiated Logout (v17+).
 * Utilise `post_logout_redirect_uri` (et non `redirect_uri` qui est l'ancien format).
 *
 * ⚠️ BUG corrigé : `keycloak.createLogoutUrl()` retourne déjà une URL avec `?`.
 * Rajouter `?redirect_uri=...` créait une URL malformée (le second `?` se retrouvait
 * dans la valeur du paramètre `client_id`).
 */
const buildKeycloakLogoutUrl = (
    keycloak: Keycloak | undefined,
    redirectUri: string,
    idToken: string | undefined
): string => {
    const providerConfig = JSON.parse(localStorage.getItem('provider') ?? 'null');
    const url =
        providerConfig?.url ??
        (keycloak as any)?.authServerUrl ??
        (keycloak as any)?.config?.url;
    const realm = providerConfig?.realm ?? (keycloak as any)?.realm;

    const clientIdFromJwt = idToken ? decodeJwtPayload(idToken)?.azp as string | undefined : undefined;
    const clientId = clientIdFromJwt ?? providerConfig?.clientId ?? (keycloak as any)?.clientId;

    if (url && realm && clientId) {
        const params = new URLSearchParams({
            post_logout_redirect_uri: redirectUri,
            client_id: clientId,
        });
        if (idToken) params.set('id_token_hint', idToken);
        return `${url}/realms/${realm}/protocol/openid-connect/logout?${params.toString()}`;
    }

    if (keycloak && typeof keycloak.createLogoutUrl === 'function') {
        const base = keycloak.createLogoutUrl();
        const separator = base.includes('?') ? '&' : '?';
        const params = new URLSearchParams({ post_logout_redirect_uri: redirectUri });
        if (idToken) params.set('id_token_hint', idToken);
        return `${base}${separator}${params.toString()}`;
    }

    return redirectUri;
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param source - Origine du logout pour le debug (ex: 'useExpireToken:inactif')
 */
export const handleLogout = async (
    dispatch: any,
    keycloak: Keycloak | undefined,
    redirectUri: string,
    userType: string | null | undefined,
    source: string = 'unknown'
) => {
    try {
        const clearStorage = () => {
            localStorage.clear();
            sessionStorage.clear();
        };

        const logout = (internalUser: boolean, idToken?: string) => {
            clearStorage();
            dispatch(settingsActions.logout(internalUser));
            setTimeout(() => {
                dispatch(settingsActions.reset());
                document.cookie =
                    'username=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

                let logoutUrl = redirectUri;
                if (!internalUser) {
                    logoutUrl = buildKeycloakLogoutUrl(keycloak, redirectUri, idToken);
                }

                window.location.replace(logoutUrl);
            }, 100);
        };

        if (userType === 'CPO_Saur') {
            logout(true);
        } else {
            if (keycloak) {
                const idToken = JSON.parse(
                    localStorage.getItem('sso-response') ?? 'null'
                )?.id_token;
                logout(false, idToken);
            }
        }
    } catch (error) {
        console.error('[LOGOUT] Erreur lors de la déconnexion :', error);
    }
};

